// Promoted from the SmallBiz Design Studio (tools/design-studio/app/data.ts) by pnpm studio:promote (EPIC #7375).
// This copy is now www-sbi code: edit it here. The studio original stays for comparison.
/**
 * Live public data for variants (S2). Every read is a GET through the lab dev server proxy
 * (vite.config.ts, PROXY_TARGETS), so it returns what www and brand serve today.
 *
 *   useEvents()        www /api/content/events        (cms-content-worker, published only)
 *   usePosts()         www /api/content/posts
 *   useTestimonials()  www /api/content/testimonials
 *   usePalettes()      brand /api/brand/palettes      (the 9 colour combinations)
 *   DEMETRIUS_POSES    the live pose masters on images.smallbizinnovation.com
 */
import { at } from './base';
import { useEffect, useState } from 'react';
import { firstOf } from '../live-strip';
import type { SiteEvent } from '../../../lib/events';

export type { SiteEvent };

export interface Post {
  id: number;
  type?: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  featured_image_url?: string | null;
  published_date?: string | null;
  category?: string | null;
}

export interface Testimonial {
  id: number;
  client_name: string;
  client_company?: string | null;
  client_role?: string | null;
  quote: string;
  featured?: number;
  // Consent flags: on only for true, 1 or '1' (client-quote.ts on()).
  show_client_name?: number | boolean | string | null;
  show_company_name?: number | boolean | string | null;
  display_order?: number | null;
}

export interface ComboSwatch {
  role: string;
  name: string;
  value: string;
}

export interface Combo {
  id: string;
  name: string;
  description: string;
  swatches: ComboSwatch[];
}

export interface Palettes {
  version: string;
  combinationSets: { id: string; label: string; combos: Combo[] }[];
}

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

const cache = new Map<string, Promise<unknown>>();

function getJson<T>(url: string): Promise<T> {
  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url, { headers: { accept: 'application/json' } }).then(async (r) => {
        if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
        return r.json();
      }),
    );
  }
  return cache.get(url) as Promise<T>;
}

function useJson<T>(url: string, pick: (body: unknown) => T): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ data: null, error: null, loading: true });
  useEffect(() => {
    let live = true;
    getJson<unknown>(url)
      .then((body) => live && setState({ data: pick(body), error: null, loading: false }))
      .catch((e: unknown) => live && setState({ data: null, error: String(e), loading: false }));
    return () => {
      live = false;
    };
    // `pick` is a pure mapper; the URL alone keys the read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return state;
}

/** The CMS list routes answer `{ success, data: [...] }`. */
const listOf = <T,>(body: unknown): T[] => ((body as { data?: T[] })?.data ?? []) as T[];

/**
 * The first row of one public CMS list route, or null when it has none (#10765, the live strip).
 * A failed read also gives null, so the caller shows its empty state.
 */
export function useFirst<T>(path: string): Loaded<T | null> {
  return useJson(at(path), (b) => firstOf<T>(b));
}

/** Upcoming published events, soonest first. */
export function useEvents(limit = 3): Loaded<SiteEvent[]> {
  return useJson(at('api/content/events?limit=20'), (b) => {
    const now = Date.now();
    return listOf<SiteEvent>(b)
      .filter((e) => e.status === 'published' && Date.parse(e.ends_at ?? e.starts_at) >= now)
      .sort((x, y) => Date.parse(x.starts_at) - Date.parse(y.starts_at))
      .slice(0, limit);
  });
}

export function usePosts(limit = 3): Loaded<Post[]> {
  return useJson(at(`api/content/posts?limit=${limit}`), (b) => listOf<Post>(b).slice(0, limit));
}

/** Featured first, then display order. */
export function useTestimonials(limit = 3): Loaded<Testimonial[]> {
  return useJson(at('api/content/testimonials?limit=50'), (b) =>
    listOf<Testimonial>(b)
      .sort((x, y) => (y.featured ?? 0) - (x.featured ?? 0) || (x.display_order ?? 99) - (y.display_order ?? 99))
      .slice(0, limit),
  );
}

export function usePalettes(): Loaded<Palettes> {
  return useJson(at('lab-proxy/brand/api/brand/palettes'), (b) => b as Palettes);
}

/**
 * The live Demetrius pose masters (sbi-agent-beacon docs/brand/mascot/03_WORKSTREAM.md, pose
 * table; each one answered 200 on 2026-10-10). The CDN has no list route, so the list is here.
 * h/w is the figure aspect, for layout before the image loads.
 */
export const DEMETRIUS_POSES = {
  Standing: { label: 'canonical standing', w: 1171, h: 3592 },
  Standing_1: { label: 'standing with briefcase', w: 1992, h: 3319 },
  Pointing: { label: 'pointing at viewer', w: 1379, h: 3627 },
  Side_Pointing: { label: 'pointing up and away', w: 1881, h: 3625 },
  Power_Pose: { label: 'hands on hips', w: 1561, h: 3572 },
  Crossed_Arms: { label: 'arms crossed', w: 1084, h: 3506 },
  Thinking: { label: 'hand to chin', w: 992, h: 3573 },
  Investigating: { label: 'magnifier to eye', w: 1601, h: 3625 },
  Walking: { label: 'mid stride', w: 1693, h: 3630 },
  Jumping: { label: 'airborne, arms up', w: 2030, h: 3560 },
  Winning: { label: 'trophy on summit', w: 2406, h: 3506 },
  Side: { label: 'left profile', w: 586, h: 3466 },
  Turn_Slightly: { label: '3/4 back turn', w: 1046, h: 3545 },
  Back: { label: 'back, arms out', w: 1171, h: 3421 },
  Back_Facing: { label: 'back, hands in pockets', w: 1068, h: 3421 },
} as const;

export type PoseName = keyof typeof DEMETRIUS_POSES;

/** The image CDN resizes and converts on the fly. */
export function imageUrl(file: string, width: number, format: 'webp' | 'png' = 'webp'): string {
  return `https://images.smallbizinnovation.com/m/${file}?width=${Math.round(width)}&format=${format}`;
}

export function poseUrl(pose: PoseName, width: number): string {
  return imageUrl(`${pose}.png`, width);
}
